/**
 * Layout for the 3D pie (`Pie3D` in Charts.jsx): a tilted pie whose slices
 * are extruded to DIFFERENT heights (taller = larger value), so the cut faces
 * between slices show, with a callout per slice — a leader line from the
 * slice to a circular badge that rings its percentage (`callout: 'badge'`).
 * The flat variant (`uniform: true, callout: 'text'`) gives every slice the
 * same thick extrusion and prints each slice's percentage + name beside the
 * pie instead (the Master Admin dashboard's look).
 *
 * Painter's order without a 3D engine: the solid is cut into horizontal
 * LEVELS at each distinct slice height. Level k (from the previous height up
 * to the k-th) contains every slice at least that tall; within a level the
 * visible side faces — the front half of each outer rim, plus the radial cut
 * faces exposed where a neighbour is shorter, back-face culled — are drawn
 * back-to-front, then the tops of the slices that END at that height. Lower
 * levels are drawn first, so a taller slice in front correctly hides what is
 * behind it, and nothing is ever painted over something nearer the viewer.
 *
 * Angles run clockwise from `startAngle` (default 15° left of 12 o'clock;
 * screen y grows downwards), so the near half of the rim is θ ∈ [0, π].
 *
 * Pure — no React — so it is unit-testable. Kept out of Charts.jsx so that
 * file only exports components (React Fast Refresh).
 */
const TAU = Math.PI * 2
const round = (n) => Math.round(n * 100) / 100

export function pie3dLayout(
  segments,
  {
    rx = 86,
    ry = 50,
    minDepth = 14,
    maxDepth = 36,
    badgeR = 25,
    badgeGap = 22,
    pad = 6,
    // A little left of 12 o'clock (like the reference look), so the first
    // slice's cut face is seen at an angle instead of edge-on.
    startAngle = -Math.PI / 2 - Math.PI / 12,
    // Every slice the same height (true) or taller for larger values (false).
    uniform = false,
    // 'badge' — leader line + ringed % badge; 'text' — % + name beside the pie.
    callout = 'badge',
    labelWidth = 118,
    labelGap = 16,
  } = {},
) {
  const live = (segments ?? []).filter((s) => Number(s?.value ?? 0) > 0)
  const total = live.reduce((a, s) => a + Number(s.value), 0)
  const textMode = callout === 'text'
  const side = textMode ? rx + labelGap + labelWidth + pad : rx + badgeGap + 2 * badgeR + pad
  const cx = round(side)
  const cy = round(pad + maxDepth + ry)
  const width = round(2 * side)
  // Callout spacing; text labels are three lines (% · name · count).
  const minGap = textMode ? 46 : 2 * badgeR + 6
  const calloutHalf = textMode ? 21 : badgeR
  // (Grown below when one side holds more callouts than the pie is tall.)
  let height = round(cy + ry + pad)
  const base0 = { width, cx, cy, rx, ry, badgeR, total }
  if (!total) return { ...base0, height, slices: [], faces: [] }

  const P = (t, z) => [cx + rx * Math.cos(t), cy + ry * Math.sin(t) - z]
  const pt = ([x, y]) => `${round(x)} ${round(y)}`
  const C = (z) => `${round(cx)} ${round(cy - z)}`
  const vmax = Math.max(...live.map((s) => Number(s.value)))
  const full = live.length === 1

  let acc = startAngle
  const slices = live.map((s, i) => {
    const v = Number(s.value)
    const a0 = acc
    const a1 = acc + (v / total) * TAU
    acc = a1
    const h = uniform ? maxDepth : round(minDepth + (maxDepth - minDepth) * (v / vmax))
    return { ...s, index: i, a0, a1, mid: (a0 + a1) / 2, share: v / total, height: h }
  })

  // ── Faces, level by level ────────────────────────────────────────────
  const faces = []
  const heights = [...new Set(slices.map((s) => s.height))].sort((a, b) => a - b)
  let zLow = 0
  for (const zHigh of heights) {
    const members = slices.filter((s) => s.height >= zHigh)
    // Side pieces above the ground overlap the level below by a hair, so
    // the stacked levels read as one seamless wall (no anti-aliasing seam).
    const zBase = zLow > 0 ? zLow - 0.6 : zLow
    const isMember = new Set(members.map((s) => s.index))
    const side = []
    for (const s of members) {
      // Outer rim — only the near half faces the viewer.
      const w0 = Math.max(s.a0, 0)
      const w1 = Math.min(s.a1, Math.PI)
      if (w1 > w0) {
        side.push({
          kind: 'wall',
          slice: s.index,
          depth: Math.sin((w0 + w1) / 2),
          d: `M ${pt(P(w0, zHigh))} A ${rx} ${ry} 0 0 1 ${pt(P(w1, zHigh))} L ${pt(P(w1, zBase))} A ${rx} ${ry} 0 0 0 ${pt(P(w0, zBase))} Z`,
        })
      }
      if (full) continue
      // Radial cut faces, exposed where the neighbour is shorter than this
      // level; culled unless they face the viewer (start edge on the left
      // half, end edge on the right half).
      const prev = (s.index - 1 + slices.length) % slices.length
      const next = (s.index + 1) % slices.length
      const cut = (a) => ({
        kind: 'cut',
        slice: s.index,
        depth: 0.5 * Math.sin(a),
        d: `M ${C(zHigh)} L ${pt(P(a, zHigh))} L ${pt(P(a, zBase))} L ${C(zBase)} Z`,
      })
      if (!isMember.has(prev) && Math.cos(s.a0) < 0) side.push(cut(s.a0))
      if (!isMember.has(next) && Math.cos(s.a1) > 0) side.push(cut(s.a1))
    }
    side.sort((a, b) => a.depth - b.depth)
    faces.push(...side)
    for (const s of members.filter((m) => m.height === zHigh)) {
      faces.push({
        kind: 'top',
        slice: s.index,
        d: full
          ? `M ${pt(P(0, zHigh))} A ${rx} ${ry} 0 1 1 ${pt(P(Math.PI, zHigh))} A ${rx} ${ry} 0 1 1 ${pt(P(0, zHigh))} Z`
          : `M ${C(zHigh)} L ${pt(P(s.a0, zHigh))} A ${rx} ${ry} 0 ${s.a1 - s.a0 > Math.PI ? 1 : 0} 1 ${pt(P(s.a1, zHigh))} Z`,
      })
    }
    zLow = zHigh
  }

  // ── Callouts: badge (leader line + ring) or text (% + name) at the side ─
  const sideOf = (s) => (Math.cos(s.mid) >= 0 ? 1 : -1)
  const busiest = Math.max(...[1, -1].map((dir) => slices.filter((s) => sideOf(s) === dir).length))
  height = round(Math.max(height, busiest * minGap + 2 * (calloutHalf + 2)))
  for (const dir of [1, -1]) {
    const group = slices
      .filter((s) => sideOf(s) === dir)
      .map((s) => {
        const [ax, ay] = [cx + rx * 0.72 * Math.cos(s.mid), cy + ry * 0.72 * Math.sin(s.mid) - s.height]
        return { s, ax, ay, y: cy + (ry + 6) * Math.sin(s.mid) - s.height / 2 }
      })
      .sort((a, b) => a.y - b.y)
    // Relax overlapping badges apart, then keep them inside the canvas.
    for (let i = 1; i < group.length; i++) {
      if (group[i].y - group[i - 1].y < minGap) group[i].y = group[i - 1].y + minGap
    }
    const lo = calloutHalf + 2
    const hi = height - calloutHalf - 2
    // Fit the whole column inside [lo, hi] by SHIFTING it (clamping single
    // labels would stack them back onto each other); if it's still too tall,
    // re-space it evenly at the minimum gap from the top.
    if (group.length) {
      const span = group[group.length - 1].y - group[0].y
      if (span > hi - lo) {
        group.forEach((g, i) => { g.y = lo + i * minGap })
      } else {
        if (group[0].y < lo) {
          const d = lo - group[0].y
          for (const g of group) g.y += d
        }
        const over = group[group.length - 1].y - hi
        if (over > 0) for (const g of group) g.y -= over
      }
    }
    for (const g of group) {
      if (textMode) {
        g.s.textAt = {
          x: round(cx + dir * (rx + labelGap)),
          y: round(g.y),
          anchor: dir > 0 ? 'start' : 'end',
          side: dir > 0 ? 'right' : 'left',
        }
        continue
      }
      const bx = cx + dir * (rx + badgeGap + badgeR)
      const elbowX = cx + dir * (rx + badgeGap * 0.35)
      g.s.badge = { x: round(bx), y: round(g.y), side: dir > 0 ? 'right' : 'left' }
      g.s.leader = `M ${round(g.ax)} ${round(g.ay)} L ${round(elbowX)} ${round(g.y)} L ${round(bx - dir * badgeR)} ${round(g.y)}`
    }
  }

  return { ...base0, height, slices, faces }
}
